import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import websocketService from '../services/websocket';

const SSHTerminal = ({ host, username, password, privateKey, onClose }) => {
  const terminalRef = useRef(null);
  const xtermInstanceRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(true);
  const [terminalReady, setTerminalReady] = useState(false);
  const [dimensions, setDimensions] = useState({ cols: 80, rows: 24 });

  // Initialize terminal
  useEffect(() => {
    let term = null;
    let fitAddon = null;
    let dataDisposer = null;
    let mounted = true;

    const initTerminal = async () => {
      if (!terminalRef.current || !mounted) return;

      try {
        // Dynamic import to avoid SSR issues
        const { Terminal } = await import('xterm');
        const { FitAddon } = await import('xterm-addon-fit');
        const { WebLinksAddon } = await import('xterm-addon-web-links');
        
        // Import CSS
        await import('xterm/css/xterm.css');

        if (!mounted || !terminalRef.current) return;

        term = new Terminal({
          cursorBlink: true,
          cursorStyle: 'block',
          fontSize: 14,
          fontFamily: '"Fira Code", Menlo, Monaco, "Courier New", monospace',
          theme: {
            background: '#0d1117',
            foreground: '#c9d1d9',
            cursor: '#58a6ff',
            cursorAccent: '#0d1117',
            selection: '#264f78',
            black: '#484f58',
            red: '#ff7b72',
            green: '#3fb950',
            yellow: '#d29922',
            blue: '#58a6ff',
            magenta: '#bc8cff',
            cyan: '#39c5cf',
            white: '#b1bac4',
            brightBlack: '#6e7681',
            brightRed: '#ffa198',
            brightGreen: '#56d364',
            brightYellow: '#e3b341',
            brightBlue: '#79c0ff',
            brightMagenta: '#d2a8ff',
            brightCyan: '#56d4dd',
            brightWhite: '#f0f6fc'
          },
          allowProposedApi: true,
          scrollback: 5000,
          convertEol: true,
          disableStdin: false
        });

        fitAddon = new FitAddon();
        const webLinksAddon = new WebLinksAddon();
        
        term.loadAddon(fitAddon);
        term.loadAddon(webLinksAddon);

        xtermInstanceRef.current = term;
        fitAddonRef.current = fitAddon;

        // Open in container
        term.open(terminalRef.current);

        // Wait for next frame then fit
        requestAnimationFrame(() => {
          if (!mounted) return;
          
          setTimeout(() => {
            if (!mounted || !fitAddon || !term) return;
            
            try {
              fitAddon.fit();
              setDimensions({ cols: term.cols, rows: term.rows });
            } catch (e) {
              console.log('Fit delayed');
            }
            
            setTerminalReady(true);
            term.writeln('\x1b[1;33m⬤ Connecting to ' + host + '...\x1b[0m');
            term.focus();

            // Connect WebSocket and SSH
            websocketService.connect();
            
            setTimeout(() => {
              if (mounted) {
                websocketService.sshConnect(host, username, password, privateKey, term.cols || 80, term.rows || 24);
              }
            }, 100);
          }, 50);
        });

        // Handle terminal input - send to SSH
        dataDisposer = term.onData((data) => {
          websocketService.sshInput(data);
        });

      } catch (err) {
        console.error('Terminal init error:', err);
        setError('Failed to initialize terminal');
      }
    };

    initTerminal();

    // Cleanup
    return () => {
      mounted = false;
      if (dataDisposer) dataDisposer.dispose();
      if (term) {
        try { term.dispose(); } catch (e) {}
      }
      xtermInstanceRef.current = null;
      fitAddonRef.current = null;
    };
  }, [host, username, password, privateKey]);

  // SSH event handlers
  useEffect(() => {
    const handleConnected = (data) => {
      setConnected(true);
      setConnecting(false);
      if (xtermInstanceRef.current) {
        xtermInstanceRef.current.writeln('\x1b[1;32m⬤ Connected to ' + data.host + ' as ' + data.username + '\x1b[0m');
        xtermInstanceRef.current.writeln('');
      }
    };

    const handleData = (data) => {
      if (xtermInstanceRef.current && data.data) {
        xtermInstanceRef.current.write(data.data);
      }
    };

    const handleClosed = () => {
      setConnected(false);
      setConnecting(false);
      if (xtermInstanceRef.current) {
        xtermInstanceRef.current.writeln('');
        xtermInstanceRef.current.writeln('\x1b[1;31m⬤ Connection closed\x1b[0m');
      }
    };

    const handleError = (data) => {
      setConnecting(false);
      setError(data.error);
      if (xtermInstanceRef.current) {
        xtermInstanceRef.current.writeln('\x1b[1;31m⬤ Error: ' + data.error + '\x1b[0m');
      }
    };

    websocketService.onSSHConnected(handleConnected);
    websocketService.onSSHData(handleData);
    websocketService.onSSHClosed(handleClosed);
    websocketService.onSSHError(handleError);

    return () => {
      websocketService.off('ssh-connected');
      websocketService.off('ssh-data');
      websocketService.off('ssh-closed');
      websocketService.off('ssh-error');
    };
  }, []);

  // Handle resize
  const handleResize = useCallback(() => {
    if (!fitAddonRef.current || !xtermInstanceRef.current) return;
    
    setTimeout(() => {
      try {
        fitAddonRef.current.fit();
        const term = xtermInstanceRef.current;
        if (term) {
          setDimensions({ cols: term.cols, rows: term.rows });
          websocketService.sshResize(term.cols, term.rows);
        }
      } catch (e) {}
    }, 100);
  }, []);

  // Window resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Fullscreen change
  useEffect(() => {
    if (terminalReady) {
      handleResize();
    }
  }, [fullscreen, terminalReady, handleResize]);

  const handleClose = () => {
    websocketService.sshDisconnect();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-b border-[#30363d]">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div 
              className="w-3 h-3 rounded-full bg-[#ff5f56] cursor-pointer hover:brightness-110" 
              onClick={handleClose}
              title="Close"
            />
            <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
            <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
          </div>
          <span className="text-sm text-[#8b949e] font-mono ml-2">
            {username}@{host}
          </span>
          {connecting && <Badge variant="secondary" className="text-xs">Connecting...</Badge>}
          {connected && <Badge className="text-xs bg-green-600">Connected</Badge>}
          {error && !connecting && <Badge variant="destructive" className="text-xs">Error</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-[#8b949e] hover:text-white hover:bg-[#30363d]"
            onClick={() => setFullscreen(!fullscreen)}
          >
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 text-[#8b949e] hover:text-white hover:bg-[#30363d]"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Terminal Container */}
      <div 
        ref={terminalRef}
        className="flex-1 overflow-hidden bg-[#0d1117]"
        style={{ 
          padding: '12px',
          height: fullscreen ? 'calc(100vh - 90px)' : '500px'
        }}
        onClick={() => xtermInstanceRef.current?.focus()}
      />

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161b22] border-t border-[#30363d]">
        <span className="text-xs text-[#8b949e]">
          Interactive SSH • vim, nano, htop supported
        </span>
        <span className="text-xs text-[#8b949e] font-mono">
          {dimensions.cols}×{dimensions.rows}
        </span>
      </div>
    </div>
  );
};

export default SSHTerminal;
